import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, User, Phone, Mail, Lock, Car, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const vehicleTypes = [
  { id: "van_small", name: "Small Van", description: "Up to 1.5 tonnes" },
  { id: "van_large", name: "Large Van", description: "Up to 3.5 tonnes" },
  { id: "flatbed", name: "Flatbed Truck", description: "Open cargo bed" },
  { id: "lowloader", name: "Low Loader", description: "Heavy machinery transport" },
  { id: "curtainside", name: "Curtain Side", description: "Easy side loading" },
  { id: "box_truck", name: "Box Truck", description: "Enclosed cargo area" },
  { id: "car_transporter", name: "Car Transporter", description: "Vehicle transport" },
];

export default function DriverRegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
    vehicle_type: "",
    license_number: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (!formData.vehicle_type) {
      toast.error("Please select a vehicle type");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/driver/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        vehicle_type: formData.vehicle_type,
        license_number: formData.license_number || null,
      });

      // Store token and driver info
      localStorage.setItem("driver_token", response.data.access_token);
      localStorage.setItem("driver", JSON.stringify(response.data.driver));

      toast.success("Registration successful! Your account is pending approval.");
      navigate("/driver/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white mb-4">
            <Truck className="h-10 w-10 text-orange-500" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              Transport Anything 247
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-white mt-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Driver Registration
          </h1>
          <p className="text-slate-400 mt-2">Join our network of professional drivers</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-orange-500" />
              Create Your Driver Account
            </CardTitle>
            <CardDescription>
              Fill in your details to register as a driver. Your account will need admin approval before you can accept jobs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    data-testid="driver-register-name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="driver-register-email"
                    placeholder="driver@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="driver-register-phone"
                    placeholder="07123 456789"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Vehicle Type */}
              <div className="space-y-2">
                <Label htmlFor="vehicle_type">Vehicle Type</Label>
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                >
                  <SelectTrigger data-testid="driver-register-vehicle">
                    <SelectValue placeholder="Select your vehicle type" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex flex-col">
                          <span>{type.name}</span>
                          <span className="text-xs text-slate-500">{type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* License Number (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="license_number">License Number (Optional)</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="license_number"
                    name="license_number"
                    data-testid="driver-register-license"
                    placeholder="DVLA License Number"
                    value={formData.license_number}
                    onChange={handleChange}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    data-testid="driver-register-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    data-testid="driver-register-confirm-password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="driver-register-submit"
                disabled={loading}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              >
                {loading ? "Creating Account..." : "Register as Driver"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <div className="text-sm text-slate-500 text-center">
              Already have an account?{" "}
              <Link to="/driver/login" className="text-orange-500 hover:underline font-medium">
                Login here
              </Link>
            </div>
            <div className="text-xs text-slate-400 text-center">
              By registering, you agree to our terms of service and commit to professional delivery standards.
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
